// Calendar math on plain `YYYY-MM-DD` strings. Operations carry a calendar
// date (no time, no time zone), so all arithmetic runs in UTC on purpose: a
// date can never shift by one day because of the viewer's time zone.
import { TIME_ZONE } from "./constants";
import type { IsoDate, IsoMonth } from "./types";

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

interface DateParts {
  y: number;
  m: number;
  d: number;
}

export function parseDate(date: IsoDate): DateParts {
  const match = DATE_RE.exec(date);
  if (!match) throw new RangeError(`Invalid date "${date}" (expected YYYY-MM-DD)`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

export function isValidDate(date: string): boolean {
  const match = DATE_RE.exec(date);
  if (!match) return false;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
}

const pad = (n: number, length = 2) => String(n).padStart(length, "0");

export const formatIsoDate = (y: number, m: number, d: number): IsoDate => `${pad(y, 4)}-${pad(m)}-${pad(d)}`;

/** Days in month `m` (1-12) of year `y`. */
export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/**
 * Adds (or subtracts) calendar months, clamping the day to the target month:
 * 2026-08-31 − 6 months = 2026-02-28.
 */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const { y, m, d } = parseDate(date);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12 + 1;
  return formatIsoDate(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const a = parseDate(from);
  const b = parseDate(to);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / MS_PER_DAY);
}

export const monthOf = (date: IsoDate): IsoMonth => date.slice(0, 7);

export const addMonthsToMonth = (month: IsoMonth, months: number): IsoMonth =>
  addMonths(`${month}-01`, months).slice(0, 7);

/** Every month from `from` to `to`, both included, oldest first (empty if `from` is later). */
export function monthRange(from: IsoMonth, to: IsoMonth): IsoMonth[] {
  const months: IsoMonth[] = [];
  for (let month = from; month <= to; month = addMonthsToMonth(month, 1)) months.push(month);
  return months;
}

export const yearOf = (month: IsoMonth) => Number(month.slice(0, 4));

/** Today's calendar date in Mexico City (or any IANA time zone). */
export function today(timeZone: string = TIME_ZONE, now: Date = new Date()): IsoDate {
  // The en-CA locale formats dates as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Calendar date (Mexico City) of a timestamp such as `createdAt`. */
export const dateOfTimestamp = (timestamp: string | Date, timeZone: string = TIME_ZONE): IsoDate =>
  today(timeZone, typeof timestamp === "string" ? new Date(timestamp) : timestamp);

// Display formatters: their output is user-facing, hence the es-MX locale.
const dateFormatter = new Intl.DateTimeFormat("es-MX", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const monthFormatter = new Intl.DateTimeFormat("es-MX", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const monthNameFormatter = new Intl.DateTimeFormat("es-MX", { month: "long", timeZone: "UTC" });
const dateTimeFormatter = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: TIME_ZONE,
});

/** `2026-03-05` → `05 mar 2026`. */
export function formatDate(date: IsoDate | null | undefined): string {
  if (!date) return "—";
  const { y, m, d } = parseDate(date.slice(0, 10));
  return dateFormatter.format(new Date(Date.UTC(y, m - 1, d)));
}

/** `2026-03` → `marzo de 2026`. */
export function formatMonth(month: IsoMonth): string {
  const { y, m } = parseDate(`${month}-01`);
  return monthFormatter.format(new Date(Date.UTC(y, m - 1, 1)));
}

/** `2026-03` → `marzo`. */
export function formatMonthName(month: IsoMonth): string {
  const { y, m } = parseDate(`${month}-01`);
  return monthNameFormatter.format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Timestamp → `3 oct 2026, 16:05` (Mexico City time). */
export function formatDateTime(timestamp: string | null | undefined): string {
  if (!timestamp) return "—";
  return dateTimeFormatter.format(new Date(timestamp));
}
